import { useState } from "react";
import { callTool } from "../api";
import { ContinueWalkPrompt, putMyWalk } from "../components/WalkPlan";
import { ratingLineage } from "../ratings";

export function CloseOutNoteCard({
  eventKey,
  accountId,
  boothNumber,
  actor,
  closed = false,
  onSaved,
  onWalkChanged,
}: {
  eventKey: string;
  accountId: string;
  boothNumber?: string | null;
  actor: string;
  closed?: boolean;
  onSaved?: (closedOut: boolean) => void;
  onWalkChanged?: (walking: boolean) => void;
}) {
  const [open, setOpen] = useState(false);
  const [body, setBody] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<"note" | "close" | "walk" | null>(null);
  const [askWalk, setAskWalk] = useState(false);

  async function save(markClosed: boolean) {
    if (!body.trim()) {
      setError("Write a note before saving.");
      return;
    }
    setBusy(markClosed ? "close" : "note");
    setError(null);
    try {
      await callTool("note_add", {
        body: body.trim(),
        note_type: "booth_touch",
        booth_number: boothNumber || undefined,
        account_id: accountId,
        actor,
        lineage: ratingLineage(),
      });
      if (markClosed) {
        await callTool("booth_visit_upsert", {
          event_key: eventKey,
          account_id: accountId,
          booth_number: boothNumber || undefined,
          owner: actor,
          status: "visited",
          notes: body.trim(),
          source: "floor",
          actor,
          lineage: ratingLineage(),
        });
      }
      setBody("");
      setAskWalk(true);
      onSaved?.(markClosed);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Save failed");
    } finally {
      setBusy(null);
    }
  }

  async function chooseWalk(walk: boolean) {
    setBusy("walk");
    setError(null);
    try {
      await putMyWalk(accountId, actor, walk);
      setAskWalk(false);
      setOpen(false);
      onWalkChanged?.(walk);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Walk update failed");
    } finally {
      setBusy(null);
    }
  }

  if (askWalk) {
    return <ContinueWalkPrompt busy={busy === "walk"} error={error} onChoose={(walk) => void chooseWalk(walk)} />;
  }

  if (closed && !open) {
    return <p className="muted closeout-status">Closed out</p>;
  }

  if (!open) {
    return (
      <button type="button" className="secondary closeout-open" onClick={() => setOpen(true)}>
        Close out
      </button>
    );
  }

  return (
    <form
      className="closeout-card"
      onSubmit={(event) => {
        event.preventDefault();
        void save(false);
      }}
    >
      <label className="field">
        Note
        <textarea
          rows={3}
          value={body}
          onChange={(event) => setBody(event.target.value)}
          placeholder="What happened at this booth?"
        />
      </label>
      {error ? <p className="err">{error}</p> : null}
      <div className="actions closeout-actions">
        <button type="submit" disabled={Boolean(busy)}>
          {busy === "note" ? "Saving…" : "Add note"}
        </button>
        <button type="button" disabled={Boolean(busy)} onClick={() => void save(true)}>
          {busy === "close" ? "Closing…" : "Add note and mark closed"}
        </button>
        <button
          type="button"
          className="secondary"
          disabled={Boolean(busy)}
          onClick={() => {
            setOpen(false);
            setError(null);
          }}
        >
          Cancel
        </button>
      </div>
    </form>
  );
}
